import { FC, useMemo, useState } from "react";
import { Select, Space, Tag, Typography } from "antd";

import {
  TV_LEAGUES,
  TV_TAG_LABELS,
  TvTag,
  TvTagType,
  toOptions,
  tvLeagueTitle,
} from "../Lib";
import { searchTvTags } from "../Providers/tv-api";
import { useDebouncedSearch } from "./useDebouncedSearch";

const key = (t: TvTag) => `${t.type}:${t.leagueId}:${t.id}`;

const tagLabel = (t: TvTag) =>
  t.type === "LEAGUE"
    ? tvLeagueTitle(t.leagueId)
    : `${t.title ?? `№${t.id}`}${t.subtitle ? ` (${t.subtitle})` : ""}`;

type Props = {
  value?: TvTag[];
  onChange?: (value: TvTag[]) => void;
  /** Лига видео — по умолчанию ищем в ней. */
  defaultLeagueId?: number | null;
  /** Какие теги можно добавить (у шоу — только клубы); по умолчанию все. */
  types?: TvTagType[];
  /** Текст, когда тегов нет. */
  emptyText?: string;
};

/**
 * Теги видео — сущности БД ФНЛ: лига, клуб, игрок, сотрудник.
 * Клубы матча и лигу бэкенд проставит сам при сохранении.
 */
export const TagsEditor: FC<Props> = ({
  value = [],
  onChange,
  defaultLeagueId,
  types,
  emptyText = "Тегов нет — лига и клубы матча появятся при сохранении",
}) => {
  const [type, setType] = useState<TvTagType>(types?.[0] ?? "TEAM");
  const [leagueId, setLeagueId] = useState<number | undefined>(
    defaultLeagueId ?? undefined
  );
  const league = leagueId ?? defaultLeagueId ?? undefined;

  const { items, loading, error, setText } = useDebouncedSearch(
    (text) => searchTvTags(type, text, league),
    `${type}:${league ?? ""}`
  );

  const taken = useMemo(() => new Set(value.map(key)), [value]);

  const add = (tag: TvTag) => {
    if (!taken.has(key(tag))) onChange?.([...value, tag]);
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Space wrap>
        {value.length === 0 && (
          <Typography.Text type="secondary">{emptyText}</Typography.Text>
        )}
        {value.map((t) => (
          <Tag
            key={key(t)}
            closable
            onClose={(e) => {
              e.preventDefault();
              onChange?.(value.filter((x) => key(x) !== key(t)));
            }}
          >
            {TV_TAG_LABELS[t.type]}: {tagLabel(t)}
          </Tag>
        ))}
      </Space>
      <Space.Compact style={{ width: "100%" }}>
        {(!types || types.length > 1) && (
          <Select
            style={{ width: 150 }}
            value={type}
            options={toOptions(TV_TAG_LABELS).filter(
              (o) => !types || types.includes(o.value as TvTagType)
            )}
            onChange={(v) => setType(v)}
          />
        )}
        {type === "LEAGUE" ? (
          <Select
            style={{ flex: 1 }}
            placeholder="Выберите лигу"
            options={TV_LEAGUES}
            value={null}
            onChange={(v: number) =>
              add({ type: "LEAGUE", leagueId: v, id: v })
            }
          />
        ) : (
          <>
            <Select
              style={{ width: 220 }}
              placeholder="Лига"
              allowClear
              options={TV_LEAGUES}
              value={league}
              onChange={(v) => setLeagueId(v)}
            />
            <Select
              style={{ flex: 1 }}
              showSearch
              value={null}
              placeholder={`Найти: ${TV_TAG_LABELS[type].toLowerCase()}`}
              filterOption={false}
              onSearch={setText}
              loading={loading}
              notFoundContent={
                error ?? (loading ? "Ищем…" : "Ничего не найдено")
              }
              options={items
                .filter((t) => !taken.has(key(t)))
                .map((t) => ({ value: key(t), label: tagLabel(t), tag: t }))}
              onChange={(_, option) => {
                const tag = (option as { tag?: TvTag } | undefined)?.tag;
                if (tag) add(tag);
              }}
            />
          </>
        )}
      </Space.Compact>
    </Space>
  );
};
